/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback } from 'react';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { notaryAPI } from '../../../../../api/notary';
import { addNotifications } from '../../../../../actions/pageActions';
import { setUsers } from '../../../../../actions/signingRoomActions';
import { useRootSelector } from '../../../../../hooks/useRootSelector';
import { CommonAPI } from '../../../../../api/common/responses';
import { UsersStatus, UserList } from './UserList';

export namespace Users {
  export interface OwnProps {
    startCall?(): Promise<void>;
    startVideoRecording?(): Promise<void>;
  }

  export type Props = OwnProps & TimerProps;
}

const styles = StyleSheet.create({
  wrapper: {},
});

const USER_LIST_STATUS_MAP: Record<keyof CommonAPI.UserDataResponse, UsersStatus> = {
  inVideoCall: { statusKey: 'inVideoCall', display: 'In video call' },
  onboarding: { statusKey: 'onboarding', display: 'Onboarding' },
  // leavers: { statusKey: 'leavers', display: 'Left' },
  missing: { statusKey: 'missing', display: 'Missing' },
};

export const _Users: React.FC<Users.Props> = ({ startCall, startVideoRecording }) => {
  const dispatch = useDispatch();
  const { callDetails, signingRoom } = useRootSelector(state => state);
  const { users } = signingRoom;

  const missingPrimaryParticipants = users.missing.filter(user => !user.primaryParticipantId);

  const showError = (err: AxiosError<{ message?: string }>) => {
    dispatch(
      addNotifications([
        {
          type: 'error',
          iconType: 'error',
          shouldAutoRemove: true,
          content: err?.response?.data?.message || err?.message,
        },
      ]),
    );
  };

  const admitBrowser = useCallback(
    async (sessionId: string) => {
      try {
        await notaryAPI.sessions.admitBrowser(callDetails!.id, sessionId);
      } catch (err) {
        showError(err as AxiosError<{ message?: string }>);
      } finally {
        const userData = await notaryAPI.calls.fetchCallUserData(callDetails!.id);
        setUsers(userData)(dispatch);
      }
    },
    [callDetails!.id],
  );

  const removeBrowser = useCallback(async (sessionId: string) => {
    try {
      await notaryAPI.sessions.removeBrowser(sessionId);
    } catch (err) {
      showError(err as AxiosError<{ message?: string }>);
    } finally {
      const userData = await notaryAPI.calls.fetchCallUserData(callDetails!.id);
      setUsers(userData)(dispatch);
    }
  }, []);

  const userGroups: Map<keyof CommonAPI.UserDataResponse, CommonAPI.Session[] | undefined> = new Map([
    ['inVideoCall', users && users.inVideoCall],
    ['onboarding', users && users.onboarding],
  ]);

  return (
    <div className={css(styles.wrapper)}>
      {users && (
        <>
          {Array.from(userGroups.keys()).map(statusKey => (
            <UserList
              usersStatus={USER_LIST_STATUS_MAP[statusKey]}
              userGroupOnOneDevice={userGroups.get(statusKey)}
              key={statusKey}
              admitBrowser={admitBrowser}
              removeBrowser={removeBrowser}
              startCall={startCall}
              startVideoRecording={startVideoRecording}
            />
          ))}
          <UserList
            usersStatus={USER_LIST_STATUS_MAP.missing}
            individualUsers={missingPrimaryParticipants}
            key={'missing'}
            admitBrowser={admitBrowser}
            removeBrowser={removeBrowser}
            startCall={startCall}
            startVideoRecording={startVideoRecording}
          />
        </>
      )}
    </div>
  );
};

export const Users = (props: Users.OwnProps) => (
  <ProvideTimers render={timerProps => <_Users {...props} {...timerProps} />} />
);
